/**
 * Initialize quantity controls for product quantity inputs
 * Handles plus/minus button clicks to dynamically update quantity
 */
let quantityObserverInitialized = false;

const initQuantity = () => {
  // Find all quantity containers
  const quantityContainers = document.querySelectorAll('.c-quantity');

  quantityContainers.forEach((container) => {
    // Skip if already initialized
    if (container.hasAttribute('data-quantity-initialized')) {
      return;
    }

    // Mark as initialized
    container.setAttribute('data-quantity-initialized', 'true');
    const plusButton = container.querySelector('.td-quantity-button.plus');
    const minusButton = container.querySelector('.td-quantity-button.min');
    const quantityInput = container.querySelector('input.qty');

    // Skip if required elements are missing
    if (!plusButton || !minusButton || !quantityInput) {
      return;
    }

    // Prevent default button behavior (form submission)
    plusButton.type = 'button';
    minusButton.type = 'button';

    // Get min, max, and step values from input attributes
    const getMinValue = () => {
      const min = quantityInput.getAttribute('min');
      return min !== null && min !== '' ? parseFloat(min) : 1;
    };

    const getMaxValue = () => {
      const max = quantityInput.getAttribute('max');
      return max !== null && max !== '' ? parseFloat(max) : null;
    };

    const getStepValue = () => {
      const step = quantityInput.getAttribute('step');
      return step !== null && step !== '' ? parseFloat(step) : 1;
    };

    // Get current quantity value
    const getCurrentValue = () => {
      const value = parseFloat(quantityInput.value) || 0;
      return isNaN(value) ? getMinValue() : value;
    };

    // Update quantity value
    const updateQuantity = (newValue) => {
      const min = getMinValue();
      const max = getMaxValue();

      // Ensure value is within bounds
      if (newValue < min) {
        newValue = min;
      }
      if (max !== null && newValue > max) {
        newValue = max;
      }

      quantityInput.value = newValue;

      // Trigger input event for WooCommerce compatibility
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Update button states (disable if at min/max)
      if (minusButton) {
        minusButton.disabled = newValue <= min;
        if (minusButton.disabled) {
          minusButton.classList.add('disabled');
        } else {
          minusButton.classList.remove('disabled');
        }
      }

      if (plusButton) {
        plusButton.disabled = max !== null && newValue >= max;
        if (plusButton.disabled) {
          plusButton.classList.add('disabled');
        } else {
          plusButton.classList.remove('disabled');
        }
      }
    };

    // Increment quantity
    const incrementQuantity = () => {
      const currentValue = getCurrentValue();
      const step = getStepValue();
      const newValue = currentValue + step;
      updateQuantity(newValue);
    };

    // Decrement quantity
    const decrementQuantity = () => {
      const currentValue = getCurrentValue();
      const step = getStepValue();
      const newValue = currentValue - step;
      updateQuantity(newValue);
    };

    // Add event listeners
    plusButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      incrementQuantity();
    });

    minusButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      decrementQuantity();
    });

    // Validate input on blur (when user types directly)
    quantityInput.addEventListener('blur', () => {
      const currentValue = getCurrentValue();
      updateQuantity(currentValue);
    });

    // Initialize button states
    const initialValue = getCurrentValue();
    updateQuantity(initialValue);
  });

  // Handle dynamic content (e.g., WooCommerce variations) - only set up once
  if (!quantityObserverInitialized) {
    quantityObserverInitialized = true;

    // Reinitialize when variation is found (if jQuery is available)
    if (typeof jQuery !== 'undefined') {
      jQuery(document.body).on('found_variation', () => {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          initQuantity();
        }, 100);
      });
    }

    // Also use MutationObserver as a fallback for dynamic content
    if (typeof MutationObserver !== 'undefined') {
      let mutationTimeout;
      const observer = new MutationObserver(() => {
        // Debounce to avoid excessive calls
        clearTimeout(mutationTimeout);
        mutationTimeout = setTimeout(() => {
          initQuantity();
        }, 150);
      });

      // Observe changes in common WooCommerce containers
      const variationForm = document.querySelector('.variations_form');
      if (variationForm) {
        observer.observe(variationForm, {
          childList: true,
          subtree: true
        });
      }

      const singleVariationWrap = document.querySelector('.single_variation_wrap');
      if (singleVariationWrap) {
        observer.observe(singleVariationWrap, {
          childList: true,
          subtree: true
        });
      }
    }
  }
};

export { initQuantity };

